/**
 * useDeferredModule — load a module after first paint, WITHOUT React.lazy.
 *
 * WHY NOT React.lazy (read this before changing it).
 *
 * App.tsx:61-215 documents a demo-day P0 from 2026-05-09: on first mount of
 * `/` in a fresh-cache session, `React.lazy` boundaries reliably stalled in
 * their fallback even after the chunk had loaded HTTP 200, the webpack module
 * was registered, and the lazy payload had flipped to Resolved. The Suspense
 * memoizedState stayed suspended and the wake-up retry never fired for the
 * INITIAL MOUNT. Navigating away and back rendered fine. Four PRs tore
 * code-splitting out of this app to escape it, and the comment there is honest
 * that this was a workaround rather than a root-cause fix.
 *
 * So the bytes stayed. A signed-out visitor downloaded the whole application
 * before the hero could paint, because the only splitting tool anyone reached
 * for was the one that was broken here.
 *
 * This hook is a different mechanism, not a retry of that one: a plain dynamic
 * `import()` fired from an effect and resolved into `useState`. There is no
 * Suspense boundary, no thenable thrown during render, and no lazy payload —
 * so the initial-mount retry path that stalled is never entered. React only
 * ever sees an ordinary state update.
 *
 * The trade is honest: the caller renders `null` (or its own placeholder)
 * until the module arrives, instead of suspending. For a route a signed-out
 * visitor never reaches, that costs nothing. Do NOT use it for content that
 * must be in the first paint — those imports stay static.
 */

import { useEffect, useState } from 'react';

type Loader<T> = () => Promise<T>;

export interface DeferredModuleState<T> {
  /** The resolved module, or null while it is still loading. */
  module: T | null;
  /** The load rejected. Callers decide whether to show anything. */
  error: unknown;
}

/**
 * Loads `loader()` once, after mount. The loader identity is deliberately NOT
 * a dependency: callers pass an inline arrow, and re-running on every render
 * would refetch forever. Pass a stable `key` if one component must be able to
 * swap which module it loads.
 */
export function useDeferredModule<T>(loader: Loader<T>, key = 'default'): DeferredModuleState<T> {
  const [state, setState] = useState<DeferredModuleState<T>>({ module: null, error: null });

  useEffect(() => {
    let alive = true;
    setState({ module: null, error: null });

    // requestIdleCallback where available so the chunk never competes with
    // first paint; a timeout keeps it bounded on a busy main thread, and
    // Safari has no rIC at all.
    const start = () => {
      loader()
        .then((mod) => {
          if (alive) setState({ module: mod, error: null });
        })
        .catch((error) => {
          if (alive) setState({ module: null, error });
        });
    };

    let handle: number | undefined;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      handle = w.requestIdleCallback(start, { timeout: 1500 });
    } else {
      handle = window.setTimeout(start, 0);
    }

    return () => {
      alive = false;
      if (handle === undefined) return;
      if (typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

export default useDeferredModule;
